import { apiRequest, apiRequestNoThrow, queryClient } from "./queryClient";
import { Block, DetailedTransaction, AddressData, Transaction, AddressStats } from "@shared/schema";
import { dateToUnixTimestamp } from "./utils";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { handleUsageLimitError } from "@/stores/usageLimitStore";

// Define the transaction details interface
export interface TransactionDetails {
  txid: string;
  version: number;
  locktime: number;
  vin: {
    txid: string;
    vout: number;
    prevout?: {
      scriptpubkey: string;
      scriptpubkey_asm: string;
      scriptpubkey_type: string;
      scriptpubkey_address: string;
      value: number;
    };
    is_coinbase: boolean;
    sequence: number;
  }[];
  vout: {
    scriptpubkey: string;
    scriptpubkey_asm: string;
    scriptpubkey_type: string;
    scriptpubkey_address: string;
    value: number;
  }[];
  size: number;
  weight: number;
  fee: number;
  status: {
    confirmed: boolean;
    block_height: number;
    block_hash: string;
    block_time: number;
  };
  blockData?: {
    id: string;
    height: number;
    version: number;
    timestamp: number;
    tx_count: number;
    size: number;
    weight: number;
    merkle_root: string;
    previousblockhash: string;
    mediantime: number;
    nonce: number;
    bits: number;
    difficulty: number;
  };
}

// Create a cache key for wallet address searches
function createAddressQueryKey(
  address: string, 
  startDate: string | null, 
  endDate: string | null, 
  pageToken?: string | null
): (string | null | undefined)[] {
  // Use the same key structure as in HomePage component for consistency
  // Include pagination token in the cache key
  return ['/api/blockchain/address', address, startDate, endDate, pageToken];
}

// In-flight request tracking to prevent duplicate simultaneous requests for the same data
const pendingRequests: Record<string, Promise<any>> = {};

// Function to search for a wallet address with cursor-based pagination
export async function searchWalletAddress(
  address: string,
  startDate: string | null,
  endDate: string | null,
  pageToken: string | null = null // Cursor-based pagination token
): Promise<AddressData> {
  // Trim any whitespace from the address
  address = address.trim();
  
  // Create a query key for this search including pagination token
  const queryKey = createAddressQueryKey(address, startDate, endDate, pageToken);
  
  // Create a serialized request key for deduplication
  const requestKey = JSON.stringify(queryKey);
  
  // Check if we already have this data in cache
  const cachedData = queryClient.getQueryData<AddressData>(queryKey);
  if (cachedData) {
    console.log('Using cached address data:', address, pageToken ? `token: ${pageToken}` : 'first page');
    return cachedData;
  }
  
  // Check if there's already an in-flight request for this exact data
  const existingRequest = pendingRequests[requestKey];
  if (existingRequest) {
    console.log('Reusing in-flight request for:', address, pageToken ? `token: ${pageToken}` : 'first page');
    return existingRequest;
  }
  
  // If not in cache or in-flight, create a new request
  try {
    // Create the request and store it
    const fetchPromise = (async () => {
      // If not in cache, fetch from API
      const params = new URLSearchParams();
      
      if (startDate) {
        params.append('startDate', startDate);
      }
      
      if (endDate) {
        params.append('endDate', endDate);
      }
      
      // Add page token if available for cursor-based pagination
      if (pageToken) {
        params.append('page_token', pageToken);
      }
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      // Ensure address is properly trimmed again before constructing the URL
      const trimmedAddress = address.trim();
      console.log(`Fetching address data for ${trimmedAddress} ${pageToken ? `with token: ${pageToken}` : '(first page)'}`);
      const response = await apiRequestNoThrow('GET', `/api/blockchain/address/${trimmedAddress}${queryString}`);
      
      if (!response.ok) {
        // Check if it's a validation error (400)
        if (response.status === 400) {
          try {
            const errorData = await response.json();
            if (errorData.code === 'INVALID_ADDRESS') {
              // Create a custom error with the error code for UI to detect
              const error = new Error(errorData.message || 'Please enter a valid Bitcoin address');
              (error as any).code = 'INVALID_ADDRESS';
              throw error;
            }
          } catch (parseError) {
            // If JSON parsing fails, fall back to generic message
            if (parseError instanceof Error && (parseError as any).code === 'INVALID_ADDRESS') {
              throw parseError;
            }
          }
          throw new Error('Invalid request');
        }

        // Check if it's a usage limit error (429)
        if (response.status === 429) {
          try {
            const errorData = await response.json();
            if (errorData.usageType) {
              handleUsageLimitError(errorData);
              throw new Error('Usage limit exceeded'); // Still throw to handle in UI
            }
          } catch (parseError) {
            // If JSON parsing fails, fall back to text
          }
        }

        const errorText = await response.text();
        throw new Error(errorText || 'Failed to fetch address data');
      }
      
      const data = await response.json();
      
      // Store the result in cache
      queryClient.setQueryData(queryKey, data);
      
      return data;
    })();
    
    // Register the in-flight request
    pendingRequests[requestKey] = fetchPromise;
    
    // Wait for the request to complete
    const result = await fetchPromise;
    
    // Remove from pending requests when done
    delete pendingRequests[requestKey];
    
    return result;
  } catch (error) {
    // Clean up on error
    delete pendingRequests[requestKey];
    throw error;
  }
}

// Function to fetch transaction details
export async function getTransactionDetails(txid: string, skipBlockData: boolean = false): Promise<TransactionDetails> {
  // Make sure txid is trimmed of whitespace
  txid = txid.trim();
  
  // Create a query key for this transaction - exactly match the format used in the TransactionDetails component
  // Include skipBlockData in the query key to prevent cache confusion
  const queryKey = ['/api/blockchain/transaction', txid, skipBlockData];
  
  // Create a serialized request key for deduplication
  const requestKey = JSON.stringify(queryKey);
  
  // Check if we already have this transaction in cache
  const cachedTransaction = queryClient.getQueryData<TransactionDetails>(queryKey);
  if (cachedTransaction) {
    console.log('Using cached transaction details:', txid);
    return cachedTransaction;
  }
  
  // Check if there's already an in-flight request for this transaction
  const existingRequest = pendingRequests[requestKey];
  if (existingRequest) {
    console.log('Reusing in-flight request for transaction:', txid);
    return existingRequest;
  }
  
  // If not in cache or in-flight, create a new request
  try {
    // Create the request and store it
    const fetchPromise = (async () => {
      console.log(`Fetching transaction data for ${txid} (skipBlockData: ${skipBlockData})`);
      // Add skipBlockData as query parameter
      const queryParams = new URLSearchParams();
      queryParams.append('skipBlockData', skipBlockData.toString());
      const response = await apiRequest('GET', `/api/blockchain/transaction/${txid}?${queryParams.toString()}`);
      
      if (!response.ok) {
        // Check if it's a usage limit error (429)
        if (response.status === 429) {
          try {
            const errorData = await response.json();
            if (errorData.usageType) {
              handleUsageLimitError(errorData);
              throw new Error('Usage limit exceeded'); // Still throw to handle in UI
            }
          } catch (parseError) {
            // If JSON parsing fails, fall back to text
          }
        }
        
        const errorText = await response.text();
        console.error(`Failed to fetch transaction details: ${errorText}`);
        throw new Error('Failed to fetch transaction details');
      }
      
      const data = await response.json();
      
      // Store the result in cache
      queryClient.setQueryData(queryKey, data);
      
      return data;
    })();
    
    // Register the in-flight request
    pendingRequests[requestKey] = fetchPromise;
    
    // Wait for the request to complete
    const result = await fetchPromise;
    
    // Remove from pending requests when done
    delete pendingRequests[requestKey];
    
    return result;
  } catch (error) {
    // Clean up on error
    delete pendingRequests[requestKey];
    throw error;
  }
}

// Function to generate CSV
export async function generateCsv(data: AddressData): Promise<void> {
  // Format the transactions for CSV - clean format without BTC labels
  const formattedTransactions = data.transactions.map(tx => {
    const date = tx.timestamp ? new Date(tx.timestamp * 1000) : new Date();
    // Format date as YYYY-MM-DD HH:MM:SS in a single field
    const formattedDate = date.toISOString().replace('T', ' ').substring(0, 19);
    const receivedAmount = tx.received_amount || 0;
    const sentAmount = tx.sent_amount || 0;
    const fee = tx.fee || 0;
    const balance = tx.balance !== undefined ? tx.balance : '';
    
    // Format values without BTC label and combine date/time into a single column
    return [
      formattedDate,
      tx.txid,
      receivedAmount > 0 ? '+' + receivedAmount.toFixed(8) : '0.00000000',
      sentAmount > 0 ? '-' + sentAmount.toFixed(8) : '0.00000000',
      fee.toFixed(8),
      balance ? balance.toFixed(8) : ''
    ].join(',');
  });

  // Create CSV header - clean column names
  const header = ['Timestamp (UTC)', 'Transaction ID', 'Amount Received', 'Amount Sent', 'Fee', 'Balance'].join(',');
  
  // Combine header and data
  const csvContent = [header, ...formattedTransactions].join('\n');
  
  // Create a blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `blockseer-transactions-${data.address.substring(0, 8)}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Function to generate PDF
export async function generatePdf(data: AddressData): Promise<void> {
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(18);
  doc.text('Blockseer Address Statement', 14, 22);
  
  // Add address info
  doc.setFontSize(12);
  doc.text(`Address: ${data.address}`, 14, 30);
  
  // Add date range if available
  if (data.startDate || data.endDate) {
    const dateRange = [];
    if (data.startDate) dateRange.push(`From: ${new Date(data.startDate).toLocaleDateString()}`);
    if (data.endDate) dateRange.push(`To: ${new Date(data.endDate).toLocaleDateString()}`);
    doc.text(dateRange.join(' '), 14, 38);
  }
  
  // Add summary stats
  doc.setFontSize(14);
  doc.text('Summary', 14, 48);
  
  const totalReceived = (data.stats.totalReceived / 100000000).toFixed(8);
  const totalSent = (data.stats.totalSent / 100000000).toFixed(8);
  const balance = (data.stats.currentBalance / 100000000).toFixed(8);
  
  doc.setFontSize(10);
  doc.text(`Total Received: ${totalReceived}`, 14, 56);
  doc.text(`Total Sent: ${totalSent}`, 14, 62);
  doc.text(`Current Balance: ${balance}`, 14, 68);
  
  // Add transactions table
  doc.setFontSize(14);
  doc.text('Transactions', 14, 78);
  
  const tableData = data.transactions.map(tx => {
    const date = tx.timestamp ? new Date(tx.timestamp * 1000) : new Date();
    // Format date as YYYY-MM-DD HH:MM:SS in a single field
    const formattedDate = date.toISOString().replace('T', ' ').substring(0, 19) + ' UTC';
    const receivedAmount = tx.received_amount || 0;
    const sentAmount = tx.sent_amount || 0;
    const fee = tx.fee || 0;
    
    return [
      formattedDate,
      tx.txid.substring(0, 10) + '...',
      receivedAmount > 0 ? '+' + receivedAmount.toFixed(8) : '0.00000000',
      sentAmount > 0 ? '-' + sentAmount.toFixed(8) : '0.00000000',
      fee.toFixed(8),
      tx.balance !== undefined ? tx.balance.toFixed(8) : 'N/A'
    ];
  });
  
  autoTable(doc, {
    startY: 82,
    head: [['Timestamp (UTC)', 'Transaction ID', 'Amount Received', 'Amount Sent', 'Fee', 'Balance']],
    body: tableData,
    theme: 'grid',
    headStyles: { fillColor: [59, 130, 246] },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 25 },
      2: { cellWidth: 35 },
      3: { cellWidth: 35 },
      4: { cellWidth: 25 },
      5: { cellWidth: 35 }
    }
  });
  
  // Add footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Generated on ${new Date().toLocaleString()} | Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2, 
      doc.internal.pageSize.height - 10, 
      { align: 'center' }
    );
  }
  
  // Save the PDF
  doc.save(`blockseer-transaction-report-${data.address.substring(0, 8)}.pdf`);
}

// Function to search for transaction hash, block hash, or block height
export async function searchBlockchain(
  searchType: "address" | "transaction" | "block_hash" | "block_height",
  query: string,
  page: number = 1,
  limit: number = 25,
  direct: boolean = false
): Promise<DetailedTransaction | Block | AddressData> {
  // Ensure the query is trimmed of whitespace
  query = query.trim();
  
  console.log(`Searching for ${query} with type ${searchType}`);
  
  // Always use direct lookup for transactions for better efficiency
  if (searchType === 'transaction' && query) {
    console.log(`Using direct transaction lookup for ${query}`);
    try {
      // Skip block data to improve performance for transaction searches
      return await getTransactionDetails(query, true);
    } catch (error) {
      console.error(`Direct transaction lookup failed, falling back to search: ${error}`);
      // Fall through to search if the direct lookup fails
    }
  }
  
  // Create a query key for this search
  const queryKey = ['/api/blockchain/search', searchType, query, page, limit];
  
  // Create a serialized request key for deduplication
  const requestKey = JSON.stringify(queryKey);
  
  // Check if we already have this data in cache
  const cachedData = queryClient.getQueryData<DetailedTransaction | Block | AddressData>(queryKey);
  if (cachedData) {
    console.log(`Using cached ${searchType} data:`, query);
    return cachedData;
  }
  
  // Check if there's already an in-flight request for this data
  const existingRequest = pendingRequests[requestKey];
  if (existingRequest) {
    console.log(`Reusing in-flight request for ${searchType}:`, query);
    return existingRequest;
  }
  
  // If not in cache or in-flight, create a new request
  try {
    // Create the request and store it
    const fetchPromise = (async () => {
      // If not in cache, fetch from API
      console.log(`Searching for ${query}`);
      
      const params = new URLSearchParams();
      params.append('query', query);
      
      // Always pass the searchType to the server
      params.append('searchType', searchType);
      
      // Add pagination parameters for address searches
      if (searchType === 'address') {
        params.append('page', page.toString());
        params.append('limit', limit.toString());
      }
      
      // Use the new search API endpoint - explicitly pass searchType to enable direct API calls
      const response = await apiRequestNoThrow('GET', `/api/blockchain/search?${params.toString()}`);
      
      if (!response.ok) {
        // Check if it's a usage limit error (429)
        if (response.status === 429) {
          try {
            const errorData = await response.json();
            if (errorData.usageType) {
              handleUsageLimitError(errorData);
              throw new Error('Usage limit exceeded'); // Still throw to handle in UI
            }
          } catch (parseError) {
            // If JSON parsing fails, fall back to text
          }
        }
        
        const errorText = await response.text();
        console.error(`Failed to fetch ${searchType} data: ${errorText}`);
        throw new Error(`Failed to fetch ${searchType} data`);
      }
      
      const searchResult = await response.json();
      
      // For non-address searches, the result includes a 'type' and 'data' property
      if (searchResult.type && searchResult.data) {
        // If it found a different type than what was requested, we will handle that in the UI
        // Just return the actual data
        
        // Store the result in cache with the correct type
        queryClient.setQueryData(queryKey, searchResult.data);
        
        return searchResult.data;
      } else {
        // Direct data response (older format)
        queryClient.setQueryData(queryKey, searchResult);
        return searchResult;
      }
    })();
    
    // Register the in-flight request
    pendingRequests[requestKey] = fetchPromise;
    
    // Wait for the request to complete
    const result = await fetchPromise;
    
    // Remove from pending requests when done
    delete pendingRequests[requestKey];
    
    return result;
  } catch (error) {
    // Clean up on error
    delete pendingRequests[requestKey];
    throw error;
  }
}
